import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft, SquarePen, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useSWRConfig } from 'swr';

import { CareCoordinatorFormModal } from '@apps/web/components/admin/care_coordinator_form_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useAdminCareCoordinator } from '@apps/web/swr_data_fetching/admin_org_record_queries';

export const Route = createFileRoute(
    '/_app/admin/care-coordinators/$care-coordinator-id',
)({
    head: () => ({
        meta: [
            {
                title: 'Care Coordinator Details | Phoebe Admin',
            },
        ],
    }),
    component: CareCoordinatorDetailPage,
});

function CareCoordinatorDetailPage() {
    const { 'care-coordinator-id': careCoordinatorId } = Route.useParams();

    return (
        <div className="h-full overflow-x-hidden overflow-y-auto">
            <div className="max-w-5xl p-6">
                <div className="mb-6">
                    <Link
                        to="/admin/care-coordinators"
                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Care Coordinators
                    </Link>
                </div>
                <CareCoordinatorDetails careCoordinatorId={careCoordinatorId} />
            </div>
        </div>
    );
}

function CareCoordinatorDetails({
    careCoordinatorId,
}: {
    careCoordinatorId: string;
}) {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const router = useRouter();
    const { mutate } = useSWRConfig();

    const {
        data: coordinator,
        isLoading,
        mutate: mutateCoordinator,
    } = useAdminCareCoordinator(careCoordinatorId);

    const handleDelete = async () => {
        if (!careCoordinatorId || !coordinator) {
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete ${coordinator.first_name} ${coordinator.last_name}? This action cannot be undone.`,
        );

        if (!confirmed) {
            return;
        }

        setIsDeleting(true);

        try {
            await getApiClient().delete({
                url: '/admin/care-coordinators/{care_coordinator_id}',
                pathParams: { care_coordinator_id: careCoordinatorId },
            });

            toast.success('Care coordinator deleted successfully', {
                persistNotification: false,
            });
            mutate(
                (key) =>
                    typeof key === 'string' &&
                    key.includes('/admin/care-coordinators'),
            );
            router.navigate({ to: '/admin/care-coordinators' });
        } catch (error) {
            toast.error('Failed to delete care coordinator', {
                persistNotification: false,
            });
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-muted-foreground flex items-center gap-2">
                    <Spinner />
                    <span>Loading care coordinator details…</span>
                </div>
            </div>
        );
    }

    if (!coordinator) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center">
                Care coordinator not found
            </div>
        );
    }

    return (
        <div className="max-w-full min-w-0 space-y-6 overflow-hidden">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">
                    Care Coordinator Details
                </h1>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditModalOpen(true)}
                        className="gap-2"
                    >
                        <SquarePen className="h-4 w-4" />
                        Edit
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="gap-2 text-red-600 hover:text-red-700"
                    >
                        <Trash2 className="h-4 w-4" />
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Badge
                        variant={
                            coordinator.is_active ? 'default' : 'secondary'
                        }
                    >
                        {coordinator.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {!coordinator.include_in_variable && (
                        <Badge variant="outline">Excluded from variable</Badge>
                    )}
                </div>
            </div>
            <Card className="p-6">
                <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Name
                            </h3>
                            <p className="mt-1">
                                {coordinator.first_name} {coordinator.last_name}
                            </p>
                        </div>
                        {coordinator.preferred_name && (
                            <div>
                                <h3 className="text-muted-foreground text-sm font-medium">
                                    Preferred Name
                                </h3>
                                <p className="mt-1">
                                    {coordinator.preferred_name}
                                </p>
                            </div>
                        )}
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Organization
                            </h3>
                            <p className="mt-1">
                                {coordinator.internal_organization_name?.trim() ||
                                    coordinator.organization_name}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Mode
                            </h3>
                            <Badge variant="outline" className="mt-1">
                                {coordinator.mode === 'sandbox'
                                    ? 'Sandbox'
                                    : 'Live'}
                            </Badge>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Email
                            </h3>
                            <p className="mt-1">
                                {coordinator.email || 'Not provided'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Phone Number
                            </h3>
                            <p className="mt-1">
                                {coordinator.phone_number || 'Not provided'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Linked User
                            </h3>
                            <p className="mt-1">
                                {coordinator.linked_user_email || 'Not linked'}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Created
                            </h3>
                            <p className="mt-1">
                                {new Date(
                                    coordinator.created_at,
                                ).toLocaleString()}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-muted-foreground text-sm font-medium">
                                Last Updated
                            </h3>
                            <p className="mt-1">
                                {new Date(
                                    coordinator.updated_at,
                                ).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
            </Card>
            <CareCoordinatorFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                mode="edit"
                careCoordinatorId={careCoordinatorId}
                onSaveSuccess={(updatedCoordinator) =>
                    mutateCoordinator(updatedCoordinator, {
                        revalidate: false,
                    })
                }
                initialData={{
                    mode: coordinator.mode,
                    organization_id: coordinator.organization_id,
                    first_name: coordinator.first_name,
                    last_name: coordinator.last_name,
                    preferred_name: coordinator.preferred_name || '',
                    email: coordinator.email || '',
                    phone_number: coordinator.phone_number || '',
                    is_active: coordinator.is_active,
                    include_in_variable: coordinator.include_in_variable,
                }}
            />
        </div>
    );
}
